/**
 * The disagreement record of the card region fork: when the builder's two
 * paths built a card differently, or either failed, one record goes to the
 * core's redline failure snapshot route as the `region_disagreement` attempt
 * stage, and one Sentry log line, `REDLINE_REGION_DISAGREEMENT`, is the index
 * to look at.
 *
 * The record carries the card's paragraphs as the add-in read them, the card,
 * the spans the offset path edited, the intended texts, both paths' results and
 * the pick; never the whole document (`document_base64` is null). Its id is
 * the sha256 of the read package, the card and where it was built, so the same
 * disagreement seen on every preview refresh is one record, and the ids sent
 * this session are kept so each is posted once.
 *
 * The log line carries the snapshot id, the path used, both pass results, both
 * change counts and both build times, and no document text.
 */
import type { RegionPathName, RegionPathResult, RegionVerdict } from "@common/utils/redline/proposal-region-referee";
import type { AcceptedTextSpan } from "@common/utils/redline/redline-engine";

import { logInfo, logWarning } from "./sentry-reporter";
import type { RedlinePayload } from "@common/utils/redline/wire/redline-payload";

import { abortRequestAfter } from "./abort-request-after";
import { getApiKey } from "./addin-auth";
import { getEnv, getPluginVersion } from "./envManager";
import { REDLINE_FAILURE_SNAPSHOT_PATH, SNAPSHOT_UPLOAD_TIMEOUT_MS } from "./redline-failure-api";
import { sha256Hex } from "./redline-snapshot";
import { safeFetch } from "./safe-fetch";

export const REGION_DISAGREEMENT_MESSAGE = "REDLINE_REGION_DISAGREEMENT";
export const REGION_DISAGREEMENT_NOT_STORED_MESSAGE = "REDLINE_REGION_DISAGREEMENT_NOT_STORED";

/** Where the card was built when the paths disagreed. */
export type RegionDisagreementPlace = "preview" | "apply";

export interface RegionDisagreementReportArgs {
    verdict: RegionVerdict | null;
    /** The card's paragraphs as the add-in read them, the package both paths built from. */
    readPackageOoxml: string;
    redline: RedlinePayload;
    coveredSpans: AcceptedTextSpan[];
    where: RegionDisagreementPlace;
}

/** The card as the record keeps it, in the core's spelling (`RegionDisagreementCard`). */
interface RegionDisagreementCardWire {
    author: string;
    paragraphs: Array<{ text_when_proposed: string; text_proposed: string }>;
    reverts: Array<{ bookmark: string; change_id: string; expected_change: { kind: "ins" | "del"; author: string; text: string }; text: string | null }>;
}

/** One path's result in the core's spelling (`RegionPathOutcome`). */
interface RegionPathOutcomeWire {
    passed: boolean;
    texts: string[];
    change_count: number;
    build_ms: number;
    error: string | null;
}

/** The `region_disagreement` attempt, in the core's spelling (`RegionDisagreementAttempt`). */
interface RegionDisagreementAttemptWire {
    stage: "region_disagreement";
    captured_at: string;
    addin_version: string;
    error_message: string;
    review_id: string | null;
    card: RegionDisagreementCardWire;
    read_package_ooxml: string;
    covered_spans: Array<{ start_offset: number; end_offset: number }>;
    intended_texts: string[];
    offset_path: RegionPathOutcomeWire;
    whole_paragraph_path: RegionPathOutcomeWire;
    used: RegionPathName | null;
    where: RegionDisagreementPlace;
}

interface RegionDisagreementSnapshotRequest {
    snapshot_id: string;
    document_base64: null;
    attempt: RegionDisagreementAttemptWire;
}

export class RedlineRegionDisagreementReport {
    /** The ids posted this session, so a disagreement seen on every preview refresh is stored once. */
    private static readonly sentSnapshotIds = new Set<string>();

    /**
     * Records the disagreement of one build, when there is one.
     *
     * CASES
     *   - no verdict (no fork ran), or the paths agreed -> nothing
     *   - a disagreement already recorded this session   -> nothing
     *   - a new disagreement                             -> the log line, then the record posted
     *
     * EDGE CASE
     *   The post fails (offline, signed out, the core rejects it): the log line is
     *   already written, and a second line says the record was not stored. The card
     *   itself is never held up by the report.
     */
    static async record({ verdict, readPackageOoxml, redline, coveredSpans, where }: RegionDisagreementReportArgs): Promise<void> {
        if (verdict === null || !verdict.disagreement) return;
        const card = RedlineRegionDisagreementReport.cardOf(redline);
        const snapshotId = await sha256Hex(`${readPackageOoxml}\n${JSON.stringify(card)}\n${where}`);
        if (RedlineRegionDisagreementReport.sentSnapshotIds.has(snapshotId)) return;
        RedlineRegionDisagreementReport.sentSnapshotIds.add(snapshotId);
        logInfo(REGION_DISAGREEMENT_MESSAGE, {
            snapshot_id: snapshotId,
            where,
            used: verdict.used ?? "none",
            offset_path_passed: verdict.offsetPath.passed,
            whole_paragraph_path_passed: verdict.wholeParagraphPath.passed,
            offset_path_change_count: verdict.offsetPath.changeCount,
            whole_paragraph_path_change_count: verdict.wholeParagraphPath.changeCount,
            offset_path_build_ms: verdict.offsetPath.buildMs,
            whole_paragraph_path_build_ms: verdict.wholeParagraphPath.buildMs,
        });
        const apiKey = getApiKey();
        if (!apiKey) {
            logWarning(REGION_DISAGREEMENT_NOT_STORED_MESSAGE, { snapshot_id: snapshotId, reason: "signed_out" });
            return;
        }
        const request: RegionDisagreementSnapshotRequest = {
            snapshot_id: snapshotId,
            document_base64: null,
            attempt: {
                stage: "region_disagreement",
                captured_at: new Date().toISOString(),
                addin_version: getPluginVersion(),
                error_message: verdict.offsetPath.error ?? verdict.wholeParagraphPath.error ?? "",
                review_id: null,
                card,
                read_package_ooxml: readPackageOoxml,
                covered_spans: coveredSpans.map((span) => ({ start_offset: span.startOffset, end_offset: span.endOffset })),
                intended_texts: verdict.intendedTexts,
                offset_path: RedlineRegionDisagreementReport.pathWireOf(verdict.offsetPath),
                whole_paragraph_path: RedlineRegionDisagreementReport.pathWireOf(verdict.wholeParagraphPath),
                used: verdict.used,
                where,
            },
        };
        try {
            await abortRequestAfter(SNAPSHOT_UPLOAD_TIMEOUT_MS, (signal) =>
                safeFetch(`${getEnv("API_BASE_URL")}${REDLINE_FAILURE_SNAPSHOT_PATH}`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
                    body: JSON.stringify(request),
                    signal,
                }));
        } catch (error) {
            logWarning(REGION_DISAGREEMENT_NOT_STORED_MESSAGE, { snapshot_id: snapshotId, reason: error instanceof Error ? error.message : String(error) });
        }
    }

    /**
     * CASES
     *   - a text card    -> its author, paragraphs and reverts
     *   - a markup card  -> its author alone: the markup's words are in the read package and the intended texts
     */
    private static cardOf(redline: RedlinePayload): RegionDisagreementCardWire {
        if (redline.kind !== "texts") return { author: redline.author, paragraphs: [], reverts: [] };
        return {
            author: redline.author,
            paragraphs: redline.paragraphs.map((paragraph) => ({ text_when_proposed: paragraph.textWhenProposed, text_proposed: paragraph.textProposed })),
            reverts: redline.reverts.map((revert) => ({ bookmark: revert.bookmark, change_id: revert.changeId, expected_change: revert.expectedChange, text: revert.text })),
        };
    }

    private static pathWireOf(path: RegionPathResult): RegionPathOutcomeWire {
        return { passed: path.passed, texts: path.texts, change_count: path.changeCount, build_ms: path.buildMs, error: path.error };
    }
}
